import { lazy } from "react";
import { Navigate } from "react-router-dom";

// project imports
import MainLayout from "layout/mainLayout";
import Loadable from "components/loadable";
import AccessManagement from "./AccessManagement";
import { authenticateUser } from "features/components/helperFunctions";

// dashboard route
const Dashboard = Loadable(lazy(() => import("features/dashboard")));

// inventory routes
const Suppliers = Loadable(lazy(() => import("features/inventory/Supplier")));

const Stocks = Loadable(lazy(() => import("features/inventory/Stocks")));
const PurchaseOrders = Loadable(
  lazy(() => import("features/inventory/PurchaseOrders"))
);
const Purchases = Loadable(lazy(() => import("features/inventory/Purchases")));

// performance routes
const Insights = Loadable(lazy(() => import("features/performance/insights")));
const OrdersDashboard = Loadable(
  lazy(() => import("features/performance/ordersDashboard"))
);
const CustomersDashboard = Loadable(
  lazy(() => import("features/performance/customersDashboard"))
);
const DeliveryDashboard = Loadable(
  lazy(() => import("features/performance/riderDashboard"))
);
const LoyaltyDashboard = Loadable(
  lazy(() => import("features/performance/loyaltyDashboard"))
);
const SeoTrafficDashboard = Loadable(
  lazy(() => import("features/performance/seoTrafficDashboard"))
);
// custom reports
const CustomReports = Loadable(lazy(() => import("features/customReports")));
const Reports = Loadable(lazy(() => import("features/customReports/listing")));
const CustomReportsBuilders = Loadable(
  lazy(() => import("features/customReports/reportsBuilder"))
);

// loyalty
const Loyalty = Loadable(lazy(() => import("features/campaigns/loyalty")));
const Coupon = Loadable(lazy(() => import("features/campaigns/coupon")));
const CouponReport = Loadable(
  lazy(() => import("features/campaigns/coupon/couponReport"))
);

// orders routing
// using same component(BusinessOrders) for both orders & future-orders
const BusinessOrders = Loadable(lazy(() => import("features/orders")));
const AbandonedCarts = Loadable(lazy(() => import("features/cartAbandonment")));

// Reviews routes
const Reviews = Loadable(lazy(() => import("features/reviews")));
const ProductReviews = Loadable(
  lazy(() => import("features/reviews/productReviews"))
);
const BusinessReviews = Loadable(
  lazy(() => import("features/reviews/businessReviews/index"))
);

// products routing
const ProductsItems = Loadable(lazy(() => import("features/products/items")));
const ProductsCategories = Loadable(
  lazy(() => import("features/products/categories"))
);
const ProductsSetup = Loadable(lazy(() => import("features/products/setup")));
const ProductsWishlist = Loadable(
  lazy(() => import("features/products/wishlist"))
);
const TimeBoundedProducts = Loadable(
  lazy(() => import("features/products/timeBoundedProducts"))
);
const ProductsBranchAssociation = Loadable(
  lazy(() => import("features/products/branchAssociation"))
);

// Product Setup children
// using same component(ProductsSetupSets) for both option & inner option sets
const ProductsSetupSets = Loadable(
  lazy(() => import("features/products/setup/options/sets"))
);
const ProductsSetupAttributes = Loadable(
  lazy(() => import("features/products/setup/attributes"))
);
const ProductsSetupFilterTags = Loadable(
  lazy(() => import("features/products/setup/filterTags"))
);
const ProductsSetupBrands = Loadable(
  lazy(() => import("features/products/setup/brands"))
);
const ProductsSetupMods = Loadable(
  lazy(() => import("features/products/setup/mods"))
);

// Customers
const Customers = Loadable(lazy(() => import("features/customers")));

// Reservations
const ReservationTabs = Loadable(lazy(() => import("features/reservations")));
const Reservations = Loadable(
  lazy(() => import("features/reservations/listing"))
);

const Subscriptions = Loadable(lazy(() => import("features/subscriptions")));

// settings routing
const Settings = Loadable(lazy(() => import("features/settings")));

const BusinessInfoSettings = Loadable(
  lazy(() => import("features/settings/businessInfo"))
);
const ProductSettings = Loadable(
  lazy(() => import("features/settings/productSettings"))
);
const OrderSettings = Loadable(
  lazy(() => import("features/settings/orderSettings"))
);
const CommunicationSettings = Loadable(
  lazy(() => import("features/settings/communicationSettings"))
);
const BusinessHoursSettings = Loadable(
  lazy(() => import("features/settings/businessHours"))
);
const ReservationSettings = Loadable(
  lazy(() => import("features/settings/reservationSettings"))
);
const IntegrationSettings = Loadable(
  lazy(() => import("features/settings/integrations"))
);

// business users
const UserSettings = Loadable(lazy(() => import("features/settings/users")));

// Integration settings children
const PaymentsIntegration = Loadable(
  lazy(() => import("features/settings/integrations/payments"))
);
const FirebaseIntegration = Loadable(
  lazy(() => import("features/settings/integrations/firebase"))
);
const GoogleAnalyticsIntegration = Loadable(
  lazy(() => import("features/settings/integrations/googleAnalytics"))
);
const CanadaPostIntegration = Loadable(
  lazy(() => import("features/settings/integrations/canadaPost"))
);
const TCSExpressIntegration = Loadable(
  lazy(() => import("features/settings/integrations/tcsExpress"))
);
const EshipperIntegration = Loadable(
  lazy(() => import("features/settings/integrations/eShipper"))
);
const BykeaIntegration = Loadable(
  lazy(() => import("features/settings/integrations/bykea"))
);
const DoorDashIntegration = Loadable(
  lazy(() => import("features/settings/integrations/DoorDash"))
);
const ShipStationIntegration = Loadable(
  lazy(() => import("features/settings/integrations/shipStation"))
);
const TwillioIntegration = Loadable(
  lazy(() => import("features/settings/integrations/twillio"))
);
const PandagoIntegration = Loadable(
  lazy(() => import("features/settings/integrations/Pandago"))
);
const SMSIntegration = Loadable(
  lazy(() => import("features/settings/integrations/smsServices"))
);

// Business-Info-Settings children
const BusinessInfoProfile = Loadable(
  lazy(() => import("features/settings/businessInfo/profile"))
);
const BusinessInfoLocations = Loadable(
  lazy(() => import("features/settings/businessInfo/locations"))
);

// Order-Settings children
const DeliveryOptions = Loadable(
  lazy(() => import("features/settings/orderSettings/deliveryOptions"))
);
const OrderOptions = Loadable(
  lazy(() => import("features/settings/orderSettings/orderOptions"))
);
const DeliveryAreas = Loadable(
  lazy(() => import("features/settings/orderSettings/deliveryAreas"))
);
const DeliveryRanges = Loadable(
  lazy(() => import("features/settings/orderSettings/deliveryRanges"))
);
const PaymentTypes = Loadable(
  lazy(() => import("features/settings/orderSettings/paymentTypes"))
);
const DeliveryChargesTabs = Loadable(
  lazy(
    () =>
      import(
        "features/settings/orderSettings/deliveryDetails/newCheckoutDeliveryChargesTabs"
      )
  )
);
// Delivery Charges
const DeliveryDetails = Loadable(
  lazy(() => import("features/settings/orderSettings/deliveryDetails"))
);
const RadiusMap = Loadable(
  lazy(() => import("features/settings/orderSettings/deliveryRadiusMap"))
);
// communication settings children
const OrdersCommunicationSettings = Loadable(
  lazy(() => import("features/settings/communicationSettings/orders"))
);
const ReservationsCommunicationSettings = Loadable(
  lazy(() => import("features/settings/communicationSettings/reservations"))
);
const OrdersEmailSetting = Loadable(
  lazy(() => import("features/settings/communicationSettings/orders/email"))
);
const OrdersSMSSetting = Loadable(
  lazy(() => import("features/settings/communicationSettings/orders/sms"))
);

// reservation settings children
const ReservationHours = Loadable(
  lazy(() => import("features/settings/reservationSettings/reservationHours"))
);
const ReservationDetails = Loadable(
  lazy(() => import("features/settings/reservationSettings/reservationDetails"))
);

// website routing
const WebsiteSeo = Loadable(lazy(() => import("features/website/SEO")));
const WebsiteDisplay = Loadable(lazy(() => import("features/website/display")));
const WebsiteTheme = Loadable(
  lazy(() => import("features/website/websiteTheme"))
);
const WebsiteStaticPages = Loadable(
  lazy(() => import("features/website/staticPages"))
);

// website display children
const WebsiteDisplayBanners = Loadable(
  lazy(() => import("features/website/display/banners"))
);
const WebsiteDisplayTicker = Loadable(
  lazy(() => import("features/website/display/ticker"))
);
const WebsiteDisplayPopup = Loadable(
  lazy(() => import("features/website/display/websitePopup"))
);
const WebsiteDisplayFavicon = Loadable(
  lazy(() => import("features/website/display/favicon"))
);

// website SEO children
const ProductSEO = Loadable(
  lazy(() => import("features/website/SEO/productSEO"))
);
const CategorySEO = Loadable(
  lazy(() => import("features/website/SEO/categorySEO"))
);
const BrandSEO = Loadable(lazy(() => import("features/website/SEO/BrandSEO")));
const PageSEO = Loadable(lazy(() => import("features/website/SEO/pageSEO")));

// POS route
const POSReports = Loadable(lazy(() => import("features/pos/reports")));
// downloadPOS
const DownloadPOS = Loadable(lazy(() => import("features/pos/downloadPOS")));
const StaffAttendance = Loadable(
  lazy(() => import("features/pos/staffAttendance"))
);

// POS reports children
const SalesSummaryPOSReport = Loadable(
  lazy(() => import("features/pos/reports/salesSummary"))
);
const DayEndPOSReport = Loadable(
  lazy(() => import("features/pos/reports/dayEnd"))
);
const CashDrawerSummaryPOSReport = Loadable(
  lazy(() => import("features/pos/reports/cashDrawerSummary"))
);
const HouseAccountListPOSReport = Loadable(
  lazy(() => import("features/pos/reports/houseAccountList"))
);
const DailySalePOSReport = Loadable(
  lazy(() => import("features/pos/reports/dailySale"))
);
const ProductsPOSReport = Loadable(
  lazy(() => import("features/pos/reports/products"))
);

// user profile
const UserProfile = Loadable(lazy(() => import("features/userProfile")));

// photo gallery
const Photos = Loadable(lazy(() => import("features/photos")));

// unauthorized access
const UnauthorizedAccess = Loadable(
  lazy(() => import("features/components/unauthorizedAccess"))
);

// invalid route
const PageNotFound = Loadable(
  lazy(() => import("features/components/pageNotFound"))
);
const PartnerAppDownload = Loadable(
  lazy(() => import("features/downloads/apps"))
);

const ActivityLogs = Loadable(lazy(() => import("features/settings/logs")));

const BillingAndSubscription = Loadable(
  lazy(() => import("features/billingAndSubscription"))
);
// ==============================|| Authorized ROUTING ||============================== //

const AccessSuppliers = AccessManagement()(Suppliers);
const AccessStocks = AccessManagement()(Stocks);
const AccessPurchaseOrder = AccessManagement()(PurchaseOrders);
const AccessPurchases = AccessManagement()(Purchases);

const AccessDashboard = AccessManagement()(Dashboard);
const AccessInsights = AccessManagement()(Insights);
const AccessOrdersDashboard = AccessManagement()(OrdersDashboard);
const AccessDeliveryDashboard = AccessManagement()(DeliveryDashboard);
// SeoTrafficDashboard
const AccessSeoTrafficDashboard = AccessManagement()(SeoTrafficDashboard);
const AccessLoyaltyDashboard = AccessManagement()(LoyaltyDashboard);
const AccessCustomersDashboard = AccessManagement()(CustomersDashboard);

// CustomReports
const AccessCustomReports = AccessManagement()(CustomReports);
const AccessReports = AccessManagement()(Reports);
const AccessCustomReportsBuilders = AccessManagement()(CustomReportsBuilders);

const AccessLoyalty = AccessManagement()(Loyalty);
const AccessCoupon = AccessManagement()(Coupon);
const AccessCouponReport = AccessManagement()(CouponReport);
const AccessBusinessOrders = AccessManagement()(BusinessOrders);
const AccessAbandonedCarts = AccessManagement()(AbandonedCarts);
const AccessReviews = AccessManagement()(Reviews);
const AccessProductReviews = AccessManagement()(ProductReviews);
const AccessBusinessReviews = AccessManagement()(BusinessReviews);
const AccessProductsItems = AccessManagement()(ProductsItems);
const AccessProductsCategories = AccessManagement()(ProductsCategories);

const AccessProductsSetup = AccessManagement()(ProductsSetup);
const AccessProductsWishlist = AccessManagement()(ProductsWishlist);
const AccessTimeBoundedProducts = AccessManagement()(TimeBoundedProducts);
const AccessProductsBranchAssociation = AccessManagement()(
  ProductsBranchAssociation
);
const AccessProductsSetupSets = AccessManagement()(ProductsSetupSets);
const AccessProductsSetupAttributes = AccessManagement()(
  ProductsSetupAttributes
);
const AccessProductsSetupFilterTags = AccessManagement()(
  ProductsSetupFilterTags
);
const AccessProductsSetupBrands = AccessManagement()(ProductsSetupBrands);
const AccessProductsSetupMods = AccessManagement()(ProductsSetupMods);
const AccessCustomers = AccessManagement()(Customers);
const AccessReservations = AccessManagement()(Reservations);
const AccessReservationTabs = AccessManagement()(ReservationTabs);
const AccessSubscriptions = AccessManagement()(Subscriptions);

const AccessSettings = AccessManagement()(Settings);
const AccessBusinessInfoSettings = AccessManagement()(BusinessInfoSettings);
const AccessProductSettings = AccessManagement()(ProductSettings);
const AccessOrderSettings = AccessManagement()(OrderSettings);
const AccessCommunicationSettings = AccessManagement()(CommunicationSettings);
const AccessBusinessHoursSettings = AccessManagement()(BusinessHoursSettings);
const AccessReservationSettings = AccessManagement()(ReservationSettings);
const AccessIntegrationSettings = AccessManagement()(IntegrationSettings);
const AccessUserSettings = AccessManagement()(UserSettings);
const AccessPaymentsIntegration = AccessManagement()(PaymentsIntegration);
const AccessFirebaseIntegration = AccessManagement()(FirebaseIntegration);
const AccessGoogleAnalyticsIntegration = AccessManagement()(
  GoogleAnalyticsIntegration
);
const AccessCanadaPostIntegration = AccessManagement()(CanadaPostIntegration);
const AccessTCSExpressIntegration = AccessManagement()(TCSExpressIntegration);
const AccessEshipperIntegration = AccessManagement()(EshipperIntegration);
const AccessBykeaIntegration = AccessManagement()(BykeaIntegration);
const AccessDoorDashIntegration = AccessManagement()(DoorDashIntegration);
const AccessShipStationIntegration = AccessManagement()(ShipStationIntegration);
const AccessTwillioIntegration = AccessManagement()(TwillioIntegration);
const AccessPandagoIntegration = AccessManagement()(PandagoIntegration);
const AccessSMSIntegration = AccessManagement()(SMSIntegration);
const AccessBusinessInfoProfile = AccessManagement()(BusinessInfoProfile);
const AccessBusinessInfoLocations = AccessManagement()(BusinessInfoLocations);

const AccessDeliveryChargesTabs = AccessManagement()(DeliveryChargesTabs);
const AccessDeliveryOptions = AccessManagement()(DeliveryOptions);
const AccessRadiusMap = AccessManagement()(RadiusMap);

const AccessOrderOptions = AccessManagement()(OrderOptions);
const AccessDeliveryAreas = AccessManagement()(DeliveryAreas);
const AccessDeliveryRanges = AccessManagement()(DeliveryRanges);
const AccessPaymentTypes = AccessManagement()(PaymentTypes);
const AccessDeliveryDetails = AccessManagement()(DeliveryDetails);

const AccessOrdersCommunicationSetting = AccessManagement()(
  OrdersCommunicationSettings
);
const AccessReservationsCommunicationSetting = AccessManagement()(
  ReservationsCommunicationSettings
);

const AccessOrdersEmailSetting = AccessManagement()(OrdersEmailSetting);
const AccessOrdersSMSSetting = AccessManagement()(OrdersSMSSetting);
const AccessReservationHours = AccessManagement()(ReservationHours);
const AccessReservationDetails = AccessManagement()(ReservationDetails);
const AccessWebsiteSeo = AccessManagement()(WebsiteSeo);
const AccessWebsiteDisplay = AccessManagement()(WebsiteDisplay);
const AccessWebsiteTheme = AccessManagement()(WebsiteTheme);

const AccessWebsiteDisplayBanners = AccessManagement()(WebsiteDisplayBanners);
const AccessWebsiteDisplayTicker = AccessManagement()(WebsiteDisplayTicker);
const AccessWebsiteDisplayPopup = AccessManagement()(WebsiteDisplayPopup);
const AccessWebsiteDisplayFavicon = AccessManagement()(WebsiteDisplayFavicon);
const AccessWebsiteStaticPages = AccessManagement()(WebsiteStaticPages);
const AccessProductSEO = AccessManagement()(ProductSEO);
const AccessCategorySEO = AccessManagement()(CategorySEO);
const AccessBrandSEO = AccessManagement()(BrandSEO);
const AccessPageSEO = AccessManagement()(PageSEO);
const AccessPOSReports = AccessManagement()(POSReports);
const AccessSalesSummaryPOSReport = AccessManagement()(SalesSummaryPOSReport);
const AccessDayEndPOSReport = AccessManagement()(DayEndPOSReport);
const AccessCashDrawerSummaryPOSReport = AccessManagement()(
  CashDrawerSummaryPOSReport
);
const AccessHouseAccountListPOSReport = AccessManagement()(
  HouseAccountListPOSReport
);
const AccessDailySalePOSReport = AccessManagement()(DailySalePOSReport);
const AccessProductsPOSReport = AccessManagement()(ProductsPOSReport);
const AccessDownloadPOS = AccessManagement()(DownloadPOS);
const AccessStaffAttendance = AccessManagement()(StaffAttendance);
const AccessPhotos = AccessManagement()(Photos);

const AccessDownloads = AccessManagement()(PartnerAppDownload);
const AccessActivityLogs = AccessManagement()(ActivityLogs);
const AccessBilling = AccessManagement()(BillingAndSubscription);
// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: "/",
  element: <MainLayout />,
  children: [
    {
      path: "/dashboard",
      element: <AccessDashboard />,
    },
    {
      path: "/photos",
      element: <AccessPhotos />,
    },
    {
      path: "/orders",
      element: <AccessBusinessOrders />,
    },
    {
      path: "/future-orders",
      element: <AccessBusinessOrders />,
    },
    {
      path: "/orders/abandoned-carts",
      element: <AccessAbandonedCarts />,
    },
    {
      path: "/",
      element: <AccessReviews />,
      children: [
        {
          path: "product-reviews",
          element: <AccessProductReviews />,
        },
        {
          path: "business-reviews",
          element: <AccessBusinessReviews />,
        },
      ],
    },
    {
      path: "/products/items",
      element: <AccessProductsItems />,
    },
    {
      path: "/products/categories",
      element: <AccessProductsCategories />,
    },
    {
      path: "/products/setup",
      element: <AccessProductsSetup />,
      children: [
        {
          path: "option-sets",
          element: <AccessProductsSetupSets />,
        },
        {
          path: "inner-option-sets",
          element: <AccessProductsSetupSets />,
        },
        {
          path: "attributes",
          element: <AccessProductsSetupAttributes />,
        },
        {
          path: "filters-tags",
          element: <AccessProductsSetupFilterTags />,
        },
        {
          path: "brands",
          element: <AccessProductsSetupBrands />,
        },
        {
          path: "mods",
          element: <AccessProductsSetupMods />,
        },
      ],
    },
    {
      path: "/products/wishlist",
      element: <AccessProductsWishlist />,
    },
    {
      path: "/time-bounded-products",
      element: <AccessTimeBoundedProducts />,
    },
    {
      path: "/products/branch-association",
      element: <AccessProductsBranchAssociation />,
    },
    {
      path: "/customers",
      element: <AccessCustomers />,
    },
    {
      path: "/campaigns/loyalty",
      element: <AccessLoyalty />,
    },
    {
      path: "/campaigns/coupons",
      element: <AccessCoupon />,
    },
    {
      path: "/campaigns/coupon-report",
      element: <AccessCouponReport />,
    },
    {
      path: "/performance/loyalty",
      element: <AccessLoyaltyDashboard />,
    },
    {
      path: "/performance/delivery",
      element: <AccessDeliveryDashboard />,
    },
    {
      path: "/performance/traffic",
      element: <AccessSeoTrafficDashboard />,
    },
    {
      path: "/performance/insights",
      element: <AccessInsights />,
    },
    {
      path: "/performance/orders",
      element: <AccessOrdersDashboard />,
    },
    {
      path: "/performance/customers",
      element: <AccessCustomersDashboard />,
    },
    {
      path: "/",
      element: <AccessCustomReports />,
      children: [
        {
          path: "custom-reports",
          element: <AccessReports />,
        },
        {
          path: "custom-reports/builder",
          element: <AccessCustomReportsBuilders />,
        },
      ],
    },
    {
      path: "/",
      element: <AccessReservationTabs />,
      children: [
        {
          path: "reservations",
          element: <AccessReservations />,
        },
        {
          path: "reservations/expired",
          element: <AccessReservations />,
        },
      ],
    },
    {
      path: "/subscriptions",
      element: <AccessSubscriptions />,
    },
    {
      path: "billing",
      element: <AccessBilling />,
    },
    {
      path: "settings",
      element: <AccessSettings />,
    },
    {
      path: "settings/business-info",
      element: <AccessBusinessInfoSettings />,
    },
    {
      path: "settings/product-settings",
      element: <AccessProductSettings />,
    },
    {
      path: "settings/order-settings",
      element: <AccessOrderSettings />,
    },
    {
      path: "settings/communication-settings",
      element: <AccessCommunicationSettings />,
    },
    {
      path: "settings/business-hours",
      element: <AccessBusinessHoursSettings />,
    },
    {
      path: "settings/reservation",
      element: <AccessReservationSettings />,
    },
    {
      path: "settings/integration",
      element: <AccessIntegrationSettings />,
    },
    {
      path: "settings/users",
      element: <AccessUserSettings />,
    },
    {
      path: "settings/activity-logs",
      element: <AccessActivityLogs />,
    },
    // business-info settings child
    {
      path: "settings/business-info/profile",
      element: <AccessBusinessInfoProfile />,
    },
    {
      path: "settings/business-info/locations",
      element: <AccessBusinessInfoLocations />,
    },
    {
      path: "settings/order-settings/delivery-options",
      element: <AccessDeliveryOptions />,
    },
    {
      path: "settings/delivery-charges",
      element: <AccessDeliveryDetails />,
    },
    {
      path: "settings/delivery-charges/map",
      element: <AccessRadiusMap />,
    },
    {
      path: "settings/order-settings/order-options",
      element: <AccessOrderOptions />,
    },
    {
      path: "settings/order-settings/delivery-areas",
      element: <AccessDeliveryAreas />,
    },
    {
      path: "settings/order-settings/delivery-ranges",
      element: <AccessDeliveryRanges />,
    },
    {
      path: "settings/order-settings/payment-types",
      element: <AccessPaymentTypes />,
    },
    {
      path: "settings/order-settings/delivery-charges",
      element: <AccessDeliveryDetails />,
    },
    {
      path: "settings/communication-settings/orders",
      element: <AccessOrdersCommunicationSetting />,
    },
    {
      path: "settings/communication-settings/reservations",
      element: <AccessReservationsCommunicationSetting />,
    },
    {
      path: "settings/communication-settings/orders/email",
      element: <AccessOrdersEmailSetting />,
    },
    {
      path: "settings/communication-settings/orders/sms",
      element: <AccessOrdersSMSSetting />,
    },
    {
      path: "settings/communication-settings/reservations/sms",
      element: <AccessOrdersSMSSetting />,
    },
    // reservation settings child
    {
      path: "settings/reservation/hours",
      element: <AccessReservationHours />,
    },
    {
      path: "settings/reservation/details",
      element: <AccessReservationDetails />,
    },
    // integration settings routes
    {
      path: "settings/integration/payments",
      element: <AccessPaymentsIntegration />,
    },
    {
      path: "settings/integration/firebase",
      element: <AccessFirebaseIntegration />,
    },
    {
      path: "settings/integration/google-analytics",
      element: <AccessGoogleAnalyticsIntegration />,
    },
    {
      path: "settings/integration/canadaPost",
      element: <AccessCanadaPostIntegration />,
    },
    {
      path: "settings/integration/TCSExpress",
      element: <AccessTCSExpressIntegration />,
    },
    {
      path: "settings/integration/eShipper",
      element: <AccessEshipperIntegration />,
    },
    {
      path: "settings/integration/Bykea",
      element: <AccessBykeaIntegration />,
    },
    {
      path: "settings/integration/ShipStation",
      element: <AccessShipStationIntegration />,
    },
    {
      path: "settings/integration/Pandago",
      element: <AccessPandagoIntegration />,
    },
    {
      path: "settings/integration/DoorDash",
      element: <AccessDoorDashIntegration />,
    },
    {
      path: "settings/integration/Sms",
      element: <AccessSMSIntegration />,
    },
    {
      path: "settings/integration/Twillio",
      element: <AccessTwillioIntegration />,
    },
    // inventory
    {
      path: "suppliers",
      element: <AccessSuppliers />,
    },
    {
      path: "stocks",
      element: <AccessStocks />,
    },
    {
      path: "purchase-order",
      element: <AccessPurchaseOrder />,
    },
    {
      path: "purchases",
      element: <AccessPurchases />,
    },
    // website routes
    {
      path: "seo",
      element: <AccessWebsiteSeo />,
      children: [
        {
          path: "product",
          element: <AccessProductSEO />,
        },
        {
          path: "category",
          element: <AccessCategorySEO />,
        },
        {
          path: "brand",
          element: <AccessBrandSEO />,
        },
        {
          path: "page",
          element: <AccessPageSEO />,
        },
      ],
    },
    {
      path: "/website",
      element: <AccessWebsiteDisplay />,
      children: [
        {
          path: "banners",
          element: <AccessWebsiteDisplayBanners />,
        },
        {
          path: "ticker",
          element: <AccessWebsiteDisplayTicker />,
        },
        {
          path: "popup",
          element: <AccessWebsiteDisplayPopup />,
        },
        {
          path: "favicon",
          element: <AccessWebsiteDisplayFavicon />,
        },
      ],
    },
    {
      path: "/website/static-pages",
      element: <AccessWebsiteStaticPages />,
    },
    {
      path: "/website/theme",
      element: <AccessWebsiteTheme />,
    },
    // POS routes
    {
      path: "pos-reports",
      element: <AccessPOSReports />,
      children: [
        {
          path: "sales-summary",
          element: <AccessSalesSummaryPOSReport />,
        },
        {
          path: "day-end",
          element: <AccessDayEndPOSReport />,
        },
        {
          path: "cash-drawer-summary",
          element: <AccessCashDrawerSummaryPOSReport />,
        },
        {
          path: "house-account-list",
          element: <AccessHouseAccountListPOSReport />,
        },
        {
          path: "daily-sale",
          element: <AccessDailySalePOSReport />,
        },
        {
          path: "products",
          element: <AccessProductsPOSReport />,
        },
      ],
    },
    {
      path: "/download-pos",
      element: <AccessDownloadPOS />,
    },
    {
      path: "/staff-attendance",
      element: <AccessStaffAttendance />,
    },
    {
      path: "/user-profile",
      element: <UserProfile />,
    },
    {
      path: "/download-partner-app",
      element: <AccessDownloads />,
    },
    {
      path: "/unauthorized-access",
      element: <UnauthorizedAccess />,
    },
    {
      path: "/*",
      element: authenticateUser() ? <PageNotFound /> : <Navigate to={"/"} />,
    },
  ],
};

export default MainRoutes;
